import {BindingDirection, RepeaterFixation} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {ComponentDesc} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {EnumUtils} from '@mintdata-wsp/common/util/EnumUtil';
import {Frames} from '@mintdata-wsp/mint-kit/api/values/frames/Frames';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {FrameCellCoordinate, FramePath, IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';

export type FramePathElement = {
    frame:IFrame|AError,
    rowIndex:number,
    colIndex:number,
    fieldName:string
}

export type FrameCoordOffset = {
    rowOffset:number,
    colOffset:number
};

export type FramePathOffsets = Array<FrameCoordOffset>;
export type FramePathElements = Array<FramePathElement>;

export type RepeaterKey = string;

export class RepeaterLevel
{
    public constructor(public readonly index:number,
                       public readonly colOffset:number,
                       public readonly rowOffset:number,
                       public readonly startIndex:number,
                       public readonly repeatDirection:BindingDirection,
                       public readonly framePath:FramePath,
                       public readonly isRepeated:boolean)
    {
    }

    public get isFirstVisible():boolean
    {
        return this.index === this.startIndex;
    }
}

export class Repeater
{
    public readonly levels:Array<RepeaterLevel> = [];

    private constructor(levels:Array<RepeaterLevel>,
                        level:RepeaterLevel)
    {
        this.levels.push(...levels, level);
    }

    public get key():RepeaterKey
    {
        const levelIndexes:Array<number> = this.levels.map((level:RepeaterLevel) => level.index);

        return levelIndexes.join('_');
    }

    public get firstOfLastLevelKey():RepeaterKey
    {
        const levelIndexes:Array<number> = this.levels.map((level:RepeaterLevel) => level === this.lastLevel ? 0 : level.index);

        return levelIndexes.join('_');
    }

    public get lastLevel():RepeaterLevel
    {
        return this.levels[this.levels.length - 1];
    }

    public get index():number
    {
        return this.lastLevel.index;
    }

    public get colOffset():number // NOTE: Should be used for top level bindings only
    {
        return this.levels.reduce(
            (prevColOffset:number, {colOffset}) => prevColOffset + colOffset,
            0
        );
    }

    public get rowOffset():number // NOTE: Should be used for top level bindings only
    {
        return this.levels.reduce(
            (prevRowOffset:number, {rowOffset}) => prevRowOffset + rowOffset,
            0
        );
    }

    public get isFirstVisible():boolean
    {
        return this.levels.reduce(
            (prevIsFirstVisible:boolean, {isFirstVisible}) => prevIsFirstVisible && isFirstVisible,
            true
        );
    }

    public originalChild(startIndex:number,
                         repeatDirection:BindingDirection,
                         framePath:FramePath,
                         isRepeated:boolean):Repeater
    {
        return new Repeater(
            this.levels,
            new RepeaterLevel(
                startIndex,
                repeatDirection === BindingDirection.Right
                    ? startIndex
                    : 0,
                repeatDirection === BindingDirection.Down
                    ? startIndex
                    : 0,
                startIndex,
                repeatDirection,
                framePath,
                isRepeated
            )
        );
    }

    public next(increment:number = 1):Repeater
    {
        return new Repeater(
            this.levels.length > 1 ? this.levels.slice(0, -1) : [],
            new RepeaterLevel(
                this.index + increment,
                this.lastLevel.repeatDirection === BindingDirection.Right
                    ? this.lastLevel.colOffset + increment
                    : this.lastLevel.colOffset,
                this.lastLevel.repeatDirection === BindingDirection.Down
                    ? this.lastLevel.rowOffset + increment
                    : this.lastLevel.rowOffset,
                this.lastLevel.startIndex,
                this.lastLevel.repeatDirection,
                this.lastLevel.framePath,
                this.lastLevel.isRepeated
            )
        );
    }

    public repeatPathInFrame(frame:IFrame, framePath:FramePath, fixation:RepeaterFixation):FramePath | AError
    {
        if( !Type.isInstanceOf(frame, Type.Frame) )
            return framePath;

        const pathElementsOrError = this.getRepeatedFramePathElements(frame, framePath, fixation);
        if( Type.isError(pathElementsOrError) )
            return pathElementsOrError as AError;

        const pathElements = pathElementsOrError as FramePathElements;
        return pathElements.map(({rowIndex, fieldName}) => new FrameCellCoordinate(fieldName, rowIndex));
    }

    public getRepeatedFramePathElements(frame:IFrame | AError, framePath:FramePath, fixation:RepeaterFixation):FramePathElements | AError
    {
        Assert.yes(framePath && framePath.length > 0, 'framePath should not be empty here');

        const framePathOffsets = this._calculateFramePathOffsets(framePath, fixation);

        // iterate through coordinates except the last one (because the last one points to the range's cells themself)
        let rowIndex = -1;
        let colIndex = -1;
        const result = [];
        for( let pathIndex = 0; ; pathIndex++ )
        {
            let data;
        
            if( Type.isInstanceOf(frame, Type.Frame) )
                data = (frame as IFrame).getDataUnsafe();
            else if( Type.isInstanceOf(frame, Type.Error) )
                data = Frames.forErrorWithContext(frame as AError, (frame as AError).errorContext).getDataUnsafe();
                
            let fields;
        
            if( Type.isInstanceOf(frame, Type.Frame) )
                fields = (frame as IFrame).getFieldsUnsafe();
            else if( Type.isInstanceOf(frame, Type.Error) )
                fields = Frames.forErrorWithContext(frame as AError, (frame as AError).errorContext).getFields();

            const coord = framePath[pathIndex];
            const {rowOffset, colOffset} = framePathOffsets[pathIndex];

            rowIndex = coord.rowIndex;
            if( rowIndex + rowOffset >= data.length )
                return AError.fromString('#InvalidNestedFrame!');

            colIndex = fields.findIndex(f => f.name === coord.fieldName);
            if( colIndex === -1 || colIndex + colOffset >= fields.length )
                return AError.fromString('#InvalidNestedFrame!');

            rowIndex += rowOffset;
            colIndex += colOffset;

            result.push({frame, rowIndex, colIndex, fieldName: fields[colIndex].name});

            // don't need last value to be an IFrame, so break right away
            if( pathIndex == framePath.length - 1 )
                break;

            const cellValue = data[rowIndex][colIndex] || null;
            if( !(Type.isInstanceOf(cellValue, Type.Frame) || Type.isInstanceOf(cellValue, Type.Error)) )
                return AError.fromString('#InvalidNestedFrame!');
            frame = cellValue as IFrame;
        }

        return result;
    }

    private _calculateFramePathOffsets(framePath:FramePath, fixation:RepeaterFixation):FramePathOffsets
    {
        const pathLength = framePath.length;
        const offsets = ArrayUtils.newArray(pathLength, () => ({rowOffset: 0, colOffset: 0}));

        // handle simple case with both direction fixed
        if( fixation == RepeaterFixation.FixedBoth )
            return offsets;

        const isFixedDown = EnumUtils.isFixedDown(fixation);
        const isFixedRight = EnumUtils.isFixedRight(fixation);

        const levels = this.levels;
        const levelsCount = levels.length;
        for( let i = 0; i < levelsCount; i++ )
        {
            const level = levels[i];
            const levelPath = level.framePath;

            let offsetIndex;
            if( i == levelsCount - 1 )
            {
                // last level allways adds to the last coord of the framePath
                offsetIndex = pathLength - 1;
            }
            else if( this._isLevelPathApplicableToFramePath(levelPath, framePath) )
            {
                // if level path head (without the last coord) is prefix of frame path
                // it adds to appropriate position in the path
                offsetIndex = levelPath.length - 1;
            }
            else if( levelPath && levelPath.length === 1 )
            {
                // special case => one-level deep repeaters add to 0-index coord,
                // so repeaters on the top-level frame add to all framePaths
                offsetIndex = 0;
            }
            else
            {
                // skip level from top-level sheet, if it's not the last one
                continue;
            }

            const offset = offsets[offsetIndex];
            offset.rowOffset += isFixedDown ? 0 : level.rowOffset;
            offset.colOffset += isFixedRight ? 0 : level.colOffset;
        }

        return offsets;
    }

    private _isLevelPathApplicableToFramePath(levelPath:FramePath, framePath:FramePath):boolean
    {
        if( !levelPath || levelPath.length > framePath.length )
            return false;

        // all coordinates, except the last one must match
        for( let i = 0; i < levelPath.length - 1; i++ )
        {
            if( !levelPath[i].isEqualTo(framePath[i]) )
                return false;
        }

        return true;
    }

    public static initial():Repeater
    {
        return Repeater.original(0, BindingDirection.Down, null, false);
    }

    public static default(componentDesc:ComponentDesc):Repeater
    {
        return Repeater.original(0, componentDesc.repeatDirection, componentDesc.repeaterPathInFrame, false);
    }

    public static original(startIndex:number,
                           repeatDirection:BindingDirection,
                           framePath:FramePath,
                           isRepeated:boolean):Repeater
    {
        return new Repeater(
            [],
            new RepeaterLevel(
                startIndex,
                repeatDirection === BindingDirection.Right
                    ? startIndex
                    : 0,
                repeatDirection === BindingDirection.Down
                    ? startIndex
                    : 0,
                startIndex,
                repeatDirection,
                framePath,
                isRepeated
            )
        );
    }
}
